import { api } from './client';

export type Difficulty = 'EASY' | 'MEDIUM' | 'HARD';

export interface SerializedBoardDto {
  placed: Array<{ row: number; col: number; letter: string; is_blank: boolean }>;
}

export interface TileDto {
  tile_id: string;
  letter: string;
  is_blank: boolean;
}

export interface PublicPuzzle {
  id: string;
  puzzle_date: string;
  difficulty: Difficulty;
  starting_board: SerializedBoardDto;
  daily_bag: TileDto[];
  move_count: number;
  target_score: number;
  fallback_used: boolean;
}

export interface LockedPuzzle {
  id: string;
  puzzle_date: string;
  difficulty: Difficulty;
  move_count: number;
  target_score: number;
  /** Stands in for daily_bag.length, which locked puzzles don't ship. */
  tile_count: number;
  fallback_used: boolean;
  locked: true;
}

export type TodayPuzzle = PublicPuzzle | LockedPuzzle;

export function isLockedPuzzle(p: TodayPuzzle | null): p is LockedPuzzle {
  return !!p && (p as LockedPuzzle).locked === true;
}

export type DayState = 'not_started' | 'in_progress' | 'won' | 'lost';

export interface DifficultyDayState {
  difficulty: Difficulty;
  state: DayState;
  lives_used: number;
  lives_remaining: number;
  best_score: number;
  target_score: number | null;
  in_progress_life_id: string | null;
  puzzle: TodayPuzzle | null;
  /** Guests only: this board needs an account today. */
  locked?: boolean;
}

export interface StreakView {
  difficulty: Difficulty;
  current_streak: number;
  max_streak: number;
  last_win_date: string | null;
  total_wins: number;
  total_played: number;
}

export async function fetchTodayPuzzles(): Promise<{ date: string; puzzles: TodayPuzzle[] }> {
  const res = await api.get('/api/puzzles/today');
  return res.data;
}

export async function fetchPuzzle(date: string, difficulty: Difficulty): Promise<PublicPuzzle> {
  const res = await api.get(`/api/puzzles/${date}/${difficulty}`);
  return res.data;
}

export async function fetchMyToday(): Promise<{
  date: string;
  difficulties: DifficultyDayState[];
}> {
  const res = await api.get('/api/me/today');
  return res.data;
}

export async function fetchMyStreaks(): Promise<{ streaks: StreakView[] }> {
  const res = await api.get('/api/me/streaks');
  return res.data;
}
